import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Menu, X, ShoppingCart, Heart, User, LogOut, Package, ChevronDown, Lock } from 'lucide-react';
// removed development Test API button
import { authService } from '../../services/auth.service';
import { cartService } from '../../services/cart.service';
import { wishlistService } from '../../services/wishlist.service';
import { productService } from '../../services/product.service';

interface HeaderProps {
  title?: string;
  onMenuClick?: () => void;
  showCategories?: boolean;
}

const Header: React.FC<HeaderProps> = ({
  title = "Green Guest",
  onMenuClick,
  showCategories = true
}) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [cartCount, setCartCount] = useState(0);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [searchSuggestions, setSearchSuggestions] = useState<{
    categories: string[];
    products: Array<{ id: number; name: string; category: string }>;
  }>({ categories: [], products: [] });
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef<HTMLFormElement>(null);
  const [topCategories, setTopCategories] = useState<Array<{ name: string; count: number }>>([]);

  // Fetch top categories by product count
  useEffect(() => {
    const fetchTopCategories = async () => {
      try {
        const products = await productService.getAllProducts();

        // Count products by category
        const categoryCounts: Record<string, number> = {};
        for (const product of products) {
          if (product.category) {
            categoryCounts[product.category] = (categoryCounts[product.category] || 0) + 1;
          }
        }

        // Sort by count (descending) and take top 4
        const sortedCategories = Object.entries(categoryCounts)
          .map(([name, count]) => ({ name, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 4);

        setTopCategories(sortedCategories);
      } catch (error) {
        // Fallback to default categories if fetch fails
        setTopCategories([
          { name: 'Trees', count: 0 },
        ]);
      }
    };

    fetchTopCategories();
  }, []);

  // Check authentication status and fetch cart/wishlist counts on component mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const userData = localStorage.getItem('user');

        if (token && userData) {
          setUser(JSON.parse(userData));

          // Fetch cart and wishlist counts
          try {
            const [cartTotal, wishlistItems] = await Promise.all([
              cartService.getCartTotal(),
              wishlistService.getUserWishlist()
            ]);

            setCartCount(cartTotal.itemCount || 0);
            setWishlistCount(wishlistItems.length || 0);
          } catch (error) {
            // Set default values if API calls fail
            setCartCount(0);
            setWishlistCount(0);
          }
        } else {
          // No token or user data, ensure counts are 0
          setUser(null);
          setCartCount(0);
          setWishlistCount(0);
        }
      } catch (error) {
        // Reset state on error
        setUser(null);
        setCartCount(0);
        setWishlistCount(0);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Check authentication status on each render to handle token expiration
  useEffect(() => {
    const checkTokenValidity = () => {
      const token = localStorage.getItem('authToken');
      if (!token && user) {
        // Token was removed but user state still exists, reset everything
        setUser(null);
        setCartCount(0);
        setWishlistCount(0);
      }
    };

    checkTokenValidity();
  }, [user]);

  // Listen for localStorage changes (e.g., when token is manually removed)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'authToken' || e.key === 'user') {
        if (!e.newValue) {
          // Token or user data was removed, reset state
          setUser(null);
          setCartCount(0);
          setWishlistCount(0);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Function to refresh cart and wishlist counts
  const refreshCounts = React.useCallback(async () => {
    try {
      const [cartTotal, wishlistItems] = await Promise.all([
        cartService.getCartTotal(),
        wishlistService.getUserWishlist()
      ]);

      setCartCount(cartTotal.itemCount || 0);
      setWishlistCount(wishlistItems.length || 0);
    } catch (error) {
    }
  }, []);

  // Listen for cart/wishlist updates
  useEffect(() => {
    // Refresh counts when component mounts or when user changes
    if (user) {
      refreshCounts();
    } else {
      // Reset counts when user is null (after logout)
      setCartCount(0);
      setWishlistCount(0);
    }
  }, [user, refreshCounts]);

  // Expose refresh function to parent components
  useEffect(() => {
    // Add event listeners for cart/wishlist updates
    const handleCartUpdate = () => refreshCounts();
    const handleWishlistUpdate = () => refreshCounts();
    const handleAuthFailed = () => {
      // Handle automatic logout when token expires
      setUser(null);
      setCartCount(0);
      setWishlistCount(0);
      setIsUserMenuOpen(false);
    };

    window.addEventListener('cartUpdated', handleCartUpdate);
    window.addEventListener('wishlistUpdated', handleWishlistUpdate);
    window.addEventListener('authFailed', handleAuthFailed);

    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
      window.removeEventListener('wishlistUpdated', handleWishlistUpdate);
      window.removeEventListener('authFailed', handleAuthFailed);
    };
  }, [refreshCounts]);

  const handleLogout = async () => {
    try {
      await authService.logout();
      setUser(null);
      setIsUserMenuOpen(false);
      // Reset cart and wishlist counts to 0
      setCartCount(0);
      setWishlistCount(0);
      navigate('/');
    } catch (error) {
      // Force logout even if API call fails
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      setUser(null);
      setIsUserMenuOpen(false);
      // Reset cart and wishlist counts to 0
      setCartCount(0);
      setWishlistCount(0);
      navigate('/');
    }
  };

  // Fetch search suggestions
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (searchQuery.trim().length < 2) {
        setSearchSuggestions({ categories: [], products: [] });
        setShowSuggestions(false);
        return;
      }

      try {
        const query = searchQuery.trim().toLowerCase();

        // Fetch categories and products
        const [categories, allProducts] = await Promise.all([
          productService.getCategories(),
          productService.getAllProducts()
        ]);

        // Filter categories that match the search query
        const matchedCategories = categories.filter(cat =>
          cat.toLowerCase().includes(query)
        ).slice(0, 5); // Limit to 5 categories

        // Filter products that match the search query
        const matchedProducts = allProducts
          .filter(product =>
            product.name.toLowerCase().includes(query) ||
            product.category.toLowerCase().includes(query)
          )
          .slice(0, 5) // Limit to 5 products
          .map(product => ({
            id: product.id,
            name: product.name,
            category: product.category
          }));

        setSearchSuggestions({
          categories: matchedCategories,
          products: matchedProducts
        });
        setShowSuggestions(matchedCategories.length > 0 || matchedProducts.length > 0);
      } catch (error) {
        setSearchSuggestions({ categories: [], products: [] });
        setShowSuggestions(false);
      }
    };

    const debounceTimer = setTimeout(() => {
      fetchSuggestions();
    }, 300); // Debounce for 300ms

    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setIsSearchExpanded(false);
      setShowSuggestions(false);
    } else {
      navigate('/search');
      setIsSearchExpanded(false);
      setShowSuggestions(false);
    }
  };

  const handleSearchClick = () => {
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setIsSearchExpanded(false);
      setShowSuggestions(false);
    } else {
      navigate('/search');
      setIsSearchExpanded(false);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (type: 'category' | 'product', value: string | number) => {
    if (type === 'category') {
      navigate(`/search?category=${encodeURIComponent(value as string)}`);
      setSearchQuery('');
    } else {
      navigate(`/product/${value}`);
      setSearchQuery('');
    }
    setShowSuggestions(false);
    setIsSearchExpanded(false);
  };

  // Map category name to different tree icons
  const getCategoryIcon = (categoryName: string, index?: number): string => {
    const treeIcons = ['🌳', '🌲', '🌴', '🌿', '🌱', '🍀', '🎋', '🌵', '🌾', '🍃', '🍂', '🍁'];

    // Use index if provided (for consistent ordering)
    if (index !== undefined) {
      return treeIcons[index % treeIcons.length];
    }

    // Otherwise, use category name hash for consistent icon per category
    const name = categoryName.toLowerCase();
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = ((hash << 5) - hash) + name.charCodeAt(i);
      hash = hash & hash; // Convert to 32-bit integer
    }
    return treeIcons[Math.abs(hash) % treeIcons.length];
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const closeUserMenu = () => {
    setIsUserMenuOpen(false);
  };

  return (
    <header className="bg-gradient-to-r from-green-50 to-emerald-50 shadow-lg sticky top-0 z-50 border-b border-green-200">
      {/* Main Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 sm:gap-3 hover:scale-105 transition-transform duration-300">
            <img
              src="/logo.png"
              alt="GreenGuest logo"
              className="h-8 w-8 sm:h-10 sm:w-10 rounded-full shadow-lg object-cover"
            />
            <h1 className="text-lg sm:text-xl lg:text-2xl font-bold bg-gradient-to-r from-green-700 to-emerald-800 bg-clip-text text-transparent">
              {title}
            </h1>
          </Link>

          {/* Desktop Search Bar */}
          <div className="hidden md:flex flex-1 max-w-md mx-4 lg:mx-8">
            <form onSubmit={handleSearch} className="relative w-full" ref={searchRef}>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setShowSuggestions(true);
                }}
                onFocus={() => {
                  if (searchQuery.trim().length >= 2) {
                    setShowSuggestions(true);
                  }
                }}
                placeholder="Search bonsai trees, tools, and accessories..."
                className="w-full px-4 py-3 pl-12 pr-12 border-2 border-green-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-400 bg-white/80 backdrop-blur-sm shadow-sm"
              />
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-green-500" />
              <button
                type="submit"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-green-500 hover:bg-green-600 text-white px-4 py-1 rounded-lg transition-colors duration-300 text-sm"
                onClick={handleSearchClick}
              >
                Search
              </button>

              {/* Search Suggestions Dropdown */}
              {showSuggestions && (searchSuggestions.categories.length > 0 || searchSuggestions.products.length > 0) && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border-2 border-green-200 rounded-xl shadow-lg z-50 max-h-96 overflow-y-auto">
                  {/* Categories Section */}
                  {searchSuggestions.categories.length > 0 && (
                    <div className="p-2">
                      <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Categories
                      </div>
                      {searchSuggestions.categories.map((category, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => handleSuggestionClick('category', category)}
                          className="w-full text-left px-4 py-2 hover:bg-green-50 rounded-lg transition-colors flex items-center gap-2"
                        >
                          <span className="text-lg">
                            {getCategoryIcon(category, index)}
                          </span>
                          <span className="text-green-700 font-medium">{category}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Products Section */}
                  {searchSuggestions.products.length > 0 && (
                    <div className="p-2 border-t border-green-100">
                      <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Products
                      </div>
                      {searchSuggestions.products.map((product) => (
                        <button
                          key={product.id}
                          type="button"
                          onClick={() => handleSuggestionClick('product', product.id)}
                          className="w-full text-left px-4 py-2 hover:bg-green-50 rounded-lg transition-colors"
                        >
                          <div className="font-medium text-gray-800">{product.name}</div>
                          <div className="text-sm text-gray-500">{product.category}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </form>
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Test API button removed */}

            {/* Desktop Navigation Links */}
            <div className="hidden lg:flex items-center gap-6">
              <Link to="/about" className="text-green-700 hover:text-green-800 transition-colors font-medium hover:scale-105">
                About
              </Link>
              <Link to="/contact" className="text-green-700 hover:text-green-800 transition-colors font-medium hover:scale-105">
                Contact
              </Link>
            </div>

            {/* Mobile Search Toggle */}
            <button
              onClick={() => setIsSearchExpanded(!isSearchExpanded)}
              className="md:hidden p-2 text-green-700 hover:text-green-800 transition-colors"
            >
              <Search className="h-5 w-5" />
            </button>

            {/* User Authentication Section */}
            {!loading && (
              <>
                {user ? (
                  // User is logged in - show user menu
                  <div className="relative">
                    <button
                      onClick={toggleUserMenu}
                      className="hidden sm:flex items-center gap-2 text-green-700 hover:text-green-800 transition-colors font-medium hover:scale-105 bg-green-100 px-3 py-2 rounded-lg"
                    >
                      <User className="h-4 w-4" />
                      <span className="max-w-24 truncate">
                        {user.firstName ? `${user.firstName} ${user.lastName}` : user.name || 'User'}
                      </span>
                      <ChevronDown className={`h-4 w-4 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {/* User Dropdown Menu */}
                    {isUserMenuOpen && (
                      <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg border border-green-200 py-2 z-50">
                        <Link
                          to="/profile"
                          className="flex items-center gap-3 px-4 py-2 text-green-700 hover:bg-green-50 transition-colors"
                          onClick={closeUserMenu}
                        >
                          <User className="h-4 w-4" />
                          Profile
                        </Link>
                        <Link
                          to="/orders"
                          className="flex items-center gap-3 px-4 py-2 text-green-700 hover:bg-green-50 transition-colors"
                          onClick={closeUserMenu}
                        >
                          <Package className="h-4 w-4" />
                          Orders
                        </Link>
                        <Link
                          to="/wishlist"
                          className="flex items-center gap-3 px-4 py-2 text-green-700 hover:bg-green-50 transition-colors"
                          onClick={closeUserMenu}
                        >
                          <Heart className="h-4 w-4" />
                          Wishlist
                        </Link>
                        <hr className="my-2 border-green-200" />
                        <button
                          onClick={handleLogout}
                          className="flex items-center gap-3 px-4 py-2 text-red-600 hover:bg-red-50 transition-colors w-full text-left"
                        >
                          <LogOut className="h-4 w-4" />
                          Logout
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  // User is not logged in - show sign in button
                  <Link to="/login" className="hidden sm:block text-green-700 hover:text-green-800 transition-colors font-medium hover:scale-105">
                    Sign In
                  </Link>
                )}
              </>
            )}

            <div
              onClick={() => {
                if (!user) {
                  alert('Please login to view your wishlist');
                  navigate('/login');
                } else {
                  navigate('/wishlist');
                }
              }}
              className={`relative transition-colors cursor-pointer ${user ? 'text-green-700 hover:text-green-800' : 'text-gray-400 hover:text-gray-500'
                }`}
              title={user ? 'View Wishlist' : 'Login to view Wishlist'}
            >
              <div className={`p-2 rounded-full shadow-sm ${user ? 'bg-gradient-to-br from-green-100 to-emerald-200' : 'bg-gray-100'
                }`}>
                {user ? <Heart className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
              </div>
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center shadow-lg">
                {wishlistCount}
              </span>
            </div>

            <div
              onClick={() => {
                if (!user) {
                  alert('Please login to view your cart');
                  navigate('/login');
                } else {
                  navigate('/cart');
                }
              }}
              className={`relative transition-colors cursor-pointer ${user ? 'text-green-700 hover:text-green-800' : 'text-gray-400 hover:text-gray-500'
                }`}
              title={user ? 'View Cart' : 'Login to view Cart'}
            >
              <div className={`p-2 rounded-full shadow-sm ${user ? 'bg-gradient-to-br from-green-100 to-emerald-200' : 'bg-gray-100'
                }`}>
                {user ? <ShoppingCart className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
              </div>
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center shadow-lg">
                {cartCount}
              </span>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden flex flex-col bg-transparent border-none cursor-pointer p-2 gap-1"
              onClick={toggleMobileMenu}
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6 text-green-600" />
              ) : (
                <Menu className="h-6 w-6 text-green-600" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Search Bar */}
      {isSearchExpanded && (
        <div className="md:hidden border-t border-green-200 bg-white px-4 py-3">
          <form onSubmit={handleSearch} className="relative" ref={searchRef}>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setShowSuggestions(true);
              }}
              onFocus={() => {
                if (searchQuery.trim().length >= 2) {
                  setShowSuggestions(true);
                }
              }}
              placeholder="Search bonsai trees, tools, and accessories..."
              className="w-full px-4 py-3 pl-12 pr-20 border-2 border-green-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-400 bg-white shadow-sm"
              autoFocus
            />
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-green-500" />
            <button
              type="submit"
              className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-green-500 hover:bg-green-600 text-white px-4 py-1 rounded-lg transition-colors duration-300 text-sm"
              onClick={handleSearchClick}
            >
              Search
            </button>

            {/* Mobile Search Suggestions Dropdown */}
            {showSuggestions && (searchSuggestions.categories.length > 0 || searchSuggestions.products.length > 0) && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white border-2 border-green-200 rounded-xl shadow-lg z-50 max-h-96 overflow-y-auto">
                {/* Categories Section */}
                {searchSuggestions.categories.length > 0 && (
                  <div className="p-2">
                    <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Categories
                    </div>
                    {searchSuggestions.categories.map((category, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleSuggestionClick('category', category)}
                        className="w-full text-left px-4 py-2 hover:bg-green-50 rounded-lg transition-colors flex items-center gap-2"
                      >
                        <span className="text-lg">
                          {getCategoryIcon(category, index)}
                        </span>
                        <span className="text-green-700 font-medium">{category}</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Products Section */}
                {searchSuggestions.products.length > 0 && (
                  <div className="p-2 border-t border-green-100">
                    <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Products
                    </div>
                    {searchSuggestions.products.map((product) => (
                      <button
                        key={product.id}
                        type="button"
                        onClick={() => handleSuggestionClick('product', product.id)}
                        className="w-full text-left px-4 py-2 hover:bg-green-50 rounded-lg transition-colors"
                      >
                        <div className="font-medium text-gray-800">{product.name}</div>
                        <div className="text-sm text-gray-500">{product.category}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </form>
        </div>
      )}

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden border-t border-green-200 bg-white shadow-lg">
          <div className="px-4 py-3 space-y-3">
            <Link
              to="/about"
              className="block text-green-700 hover:text-green-800 transition-colors font-medium py-2"
              onClick={closeMobileMenu}
            >
              About
            </Link>
            <Link
              to="/contact"
              className="block text-green-700 hover:text-green-800 transition-colors font-medium py-2"
              onClick={closeMobileMenu}
            >
              Contact
            </Link>
            {user ? (
              <>
                <Link
                  to="/profile"
                  className="block text-green-700 hover:text-green-800 transition-colors font-medium py-2"
                  onClick={closeMobileMenu}
                >
                  Profile
                </Link>
                <Link
                  to="/orders"
                  className="block text-green-700 hover:text-green-800 transition-colors font-medium py-2"
                  onClick={closeMobileMenu}
                >
                  Orders
                </Link>
                <button
                  onClick={() => {
                    handleLogout();
                    closeMobileMenu();
                  }}
                  className="block text-red-600 hover:text-red-700 transition-colors font-medium py-2 w-full text-left"
                >
                  Logout
                </button>
              </>
            ) : (
              <Link
                to="/login"
                className="block text-green-700 hover:text-green-800 transition-colors font-medium py-2"
                onClick={closeMobileMenu}
              >
                Sign In
              </Link>
            )}
            <Link
              to="/search"
              className="block text-green-700 hover:text-green-800 transition-colors font-medium py-2"
              onClick={closeMobileMenu}
            >
              Search All
            </Link>
            <div
              onClick={() => {
                closeMobileMenu();
                if (!user) {
                  alert('Please login to view your cart');
                  navigate('/login');
                } else {
                  navigate('/cart');
                }
              }}
              className="block text-green-700 hover:text-green-800 transition-colors font-medium py-2 cursor-pointer"
            >
              Cart ({cartCount})
            </div>
            <div
              onClick={() => {
                closeMobileMenu();
                if (!user) {
                  alert('Please login to view your wishlist');
                  navigate('/login');
                } else {
                  navigate('/wishlist');
                }
              }}
              className="block text-green-700 hover:text-green-800 transition-colors font-medium py-2 cursor-pointer"
            >
              Wishlist ({wishlistCount})
            </div>
          </div>
        </div>
      )}

      {/* Category Navigation */}
      {showCategories && (
        <div className="border-t border-green-200 bg-gradient-to-r from-green-100/50 to-emerald-100/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center">
            {/* Desktop Categories */}
            <nav className="hidden md:flex items-center justify-center py-4 w-full">
              <ul className="flex list-none m-0 p-0 gap-4 lg:gap-8 flex-wrap justify-center items-center w-full">
                <li>
                  <Link
                    to="/"
                    className="text-green-700 no-underline font-medium transition-all duration-300 hover:text-green-800 hover:scale-105 flex items-center gap-1"
                  >
                    🏠 Home
                  </Link>
                </li>
                {topCategories.length > 0 ? (
                  topCategories.map((category, index) => (
                    <li key={category.name}>
                      <Link
                        to={`/search?category=${encodeURIComponent(category.name)}`}
                        className="text-green-700 no-underline font-medium transition-all duration-300 hover:text-green-800 hover:scale-105 flex items-center gap-1"
                      >
                        {getCategoryIcon(category.name, index)} {category.name}
                      </Link>
                    </li>
                  ))
                ) : (
                  // Fallback while loading
                  <>
                    <li><Link to="/search?category=Trees" className="text-green-700 no-underline font-medium transition-all duration-300 hover:text-green-800 hover:scale-105 flex items-center gap-1">🌳 Trees</Link></li>
                  </>
                )}
                <li><Link to="/search" className="text-green-700 no-underline font-medium transition-all duration-300 hover:text-green-800 hover:scale-105 flex items-center gap-1">🔍 Search All</Link></li>
                {user && (
                  <li><Link to="/orders" className="text-green-700 no-underline font-medium transition-all duration-300 hover:text-green-800 hover:scale-105 flex items-center gap-1">📦 Orders</Link></li>
                )}
              </ul>
            </nav>

            {/* Mobile Categories */}
            <div className="md:hidden py-3 overflow-x-auto w-full">
              <div className="flex space-x-4 px-2 min-w-max justify-center items-center">
                <Link
                  to="/"
                  className="text-green-700 no-underline font-medium text-sm whitespace-nowrap flex items-center gap-1"
                >
                  🏠 Home
                </Link>
                {topCategories.length > 0 ? (
                  topCategories.map((category, index) => (
                    <Link
                      key={category.name}
                      to={`/search?category=${encodeURIComponent(category.name)}`}
                      className="text-green-700 no-underline font-medium text-sm whitespace-nowrap flex items-center gap-1"
                    >
                      {getCategoryIcon(category.name, index)} {category.name}
                    </Link>
                  ))
                ) : (
                  // Fallback while loading
                  <>
                    <Link to="/search?category=Trees" className="text-green-700 no-underline font-medium text-sm whitespace-nowrap flex items-center gap-1">🌳 Trees</Link>
                  </>
                )}
                <Link to="/search" className="text-green-700 no-underline font-medium text-sm whitespace-nowrap flex items-center gap-1">🔍 Search</Link>
                {user && (
                  <Link to="/orders" className="text-green-700 no-underline font-medium text-sm whitespace-nowrap flex items-center gap-1">📦 Orders</Link>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close user menu */}
      {isUserMenuOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={closeUserMenu}
        />
      )}
    </header>
  );
};

export default Header; 